import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { LOCAL_ENV_KEY, getSupabaseConfig } from './supabaseConfig';

/**
 * Supabase Configuration with Hostname-Based Environment Selection
 *
 * Automatically selects the correct Supabase project based on hostname:
 * - demo.hollywoodtrucks.com → DEMO Supabase (fuxqvfyxgxybrilrlwqz)
 * - app.hollywoodtrucks.com → PRODUCTION Supabase (wzpdizjzvddtvzvwbihw)
 * - Vercel preview deployments → explicit VITE_SUPABASE_ENVIRONMENT=demo required
 * - localhost → Defaults to PRODUCTION, switchable via login toggle
 */

/**
 * Set local development environment preference
 * Only works on localhost - triggers page reload to reinitialize Supabase client
 */
export function setLocalSupabaseEnv(env: 'demo' | 'production'): void {
  if (typeof window === 'undefined') return;

  const hostname = window.location.hostname;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    console.warn('[Supabase] setLocalSupabaseEnv only works on localhost');
    return;
  }

  localStorage.setItem(LOCAL_ENV_KEY, env);
  console.log(`[Supabase] Switching to ${env.toUpperCase()} environment...`);
  window.location.reload();
}

/**
 * Get current local development environment preference
 */
export function getLocalSupabaseEnv(): 'demo' | 'production' {
  if (typeof window === 'undefined') return 'production';
  return (localStorage.getItem(LOCAL_ENV_KEY) as 'demo' | 'production') || 'production';
}

// Get configuration based on hostname
const config = getSupabaseConfig();
const supabaseUrl = config.url;
const supabaseAnonKey = config.anonKey;
export const supabaseConfigurationError = config.error ?? null;

// Debug: Log what config we got and what localStorage has
if (typeof window !== 'undefined' && !supabaseConfigurationError) {
  const lsValue = localStorage.getItem('local_supabase_env');
  console.log('[Supabase Init] Module loaded with:', {
    hostname: window.location.hostname,
    localStorageValue: lsValue,
    selectedEnv: config.env,
    supabaseUrl: config.url
  });

  // IMPORTANT: Check for stale auth tokens from a different Supabase project
  // This can happen when switching between demo/production environments
  // The token will fail to refresh if it's for a different project
  const authKeys = Object.keys(localStorage).filter(key => key.startsWith('sb-') && key.includes('-auth-'));
  authKeys.forEach(key => {
    // Extract project ref from key (format: sb-{projectRef}-auth-token)
    const match = key.match(/^sb-([^-]+)-auth-/);
    if (match) {
      const tokenProjectRef = match[1];
      const currentProjectRef = config.url.replace('https://', '').split('.')[0];
      if (tokenProjectRef !== currentProjectRef) {
        console.warn(`[Supabase Init] Clearing stale auth token for project ${tokenProjectRef} (current: ${currentProjectRef})`);
        localStorage.removeItem(key);
      }
    }
  });
}

// Export current environment for UI display
export const currentSupabaseEnv = config.env;

// Create Supabase client - hostname determines which project to connect to
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: !supabaseConfigurationError,
    autoRefreshToken: !supabaseConfigurationError,
    ...(supabaseConfigurationError ? { detectSessionInUrl: false } : {}),
  },
  // Keep imports safe while preventing even accidental SDK calls from reaching
  // a project when the startup screen reports a configuration error.
  ...(supabaseConfigurationError ? {
    global: { fetch: async () => { throw new Error(supabaseConfigurationError); } },
  } : {}),
});

// Re-export Database type for convenience
export type { Database };
